import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Fade,
  Grow
} from '@mui/material';
import {
  CheckCircle,
  CheckCircleOutline,
  Error,
  Receipt,
  Person,
  Email,
  CalendarToday,
  CreditCard
} from '@mui/icons-material';
import { PaymentTokenManager, lookupPaymentByTransaction } from '../../services/paymentApi';
import './styles.scss';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [isMockData, setIsMockData] = useState(false);

  useEffect(() => {
    // Capture HighLevel payment parameters from URL
    const transactionId = searchParams.get('transactionId');
    const authorizationCode = searchParams.get('authorizationCode');
    const paymentStatus = searchParams.get('status');
    const amount = searchParams.get('amount');
    const orderId = searchParams.get('orderId');
    
    // Also check for token-based parameters (for order-summary compatibility)
    const token = searchParams.get('token');
    const webhookToken = searchParams.get('webhook_token');
    
    
    
    // Determine if this is a token-based redirect (order-summary style) or parameter-based
    const isTokenBased = token || webhookToken;
    const isParameterBased = transactionId || authorizationCode || paymentStatus;
    
    // Check if we have payment data in session storage
    const storedPaymentData = PaymentTokenManager.getPaymentData();
    const storedToken = PaymentTokenManager.getToken();
    
    // Debug stored data
    
    // Handle token-based redirect (order-summary compatibility)
    if (isTokenBased) {
      const tokenToUse = token || webhookToken;
      
      // Call backend API to verify token and get payment details
      const apiUrl = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8080' 
          : 'https://hdg-api-service-485830218751.us-central1.run.app');
      fetch(`${apiUrl}/api/verify-payment/${tokenToUse}`)
        .then(response => response.json())
        .then(data => {
          if (data.verified) {
            const paymentDataToStore = {
              ...data.payment,
              transactionId: tokenToUse, // Use token as transaction ID
              authorizationCode: data.payment.authorizationCode || 'N/A',
              status: 'completed',
              amount: data.payment.paymentAmount,
              orderId: tokenToUse,
              email: data.payment.contactEmail || storedPaymentData?.email
            };
            
            // Store payment data in sessionStorage for registration component
            PaymentTokenManager.storeToken(tokenToUse);
            PaymentTokenManager.storePaymentData(paymentDataToStore);
            
            setPaymentData(paymentDataToStore);
            setLoading(false);
          } else {
            setError(data.error || 'Payment verification failed');
            setLoading(false);
          }
        })
        .catch(error => {
          setError('Failed to verify payment');
          setLoading(false);
        });
      return; // Exit early for token-based flow
    }
    
    // After checking for token-based redirect, add this logic for parameter-based:
    if (isParameterBased && !isTokenBased) {
      const params = {
        transactionId,
        authorizationCode,
        orderId,
        status: paymentStatus,
        amount
      };
      
      // Call backend API to get token from HighLevel transaction data
      lookupPaymentByTransaction(params)
        .then(result => {
          if (result.success && result.token) {
            // Store the payment data and token
            PaymentTokenManager.storeToken(result.token);
            PaymentTokenManager.storePaymentData(result.payment);
            
            // Set payment data for display
            setPaymentData({
              ...result.payment,
              transactionId: transactionId || result.payment.transactionId,
              authorizationCode: authorizationCode || result.payment.authorizationCode,
              orderId: orderId || result.payment.orderId,
              amount: amount ? parseFloat(amount) : result.payment.paymentAmount,
              planType: result.payment.productTier || result.payment.planType,
              email: result.payment.contactEmail || storedPaymentData?.email,
              customerInfo: {
                firstName: result.payment.contactName?.split(' ')[0] || '',
                lastName: result.payment.contactName?.split(' ')[1] || ''
              },
              timestamp: result.payment.createdAt || new Date().toISOString()
            });
            setLoading(false);
          } else {
            setError(result.error || 'Payment verification failed. Please contact support.');
            setLoading(false);
          }
        })
        .catch(error => {
          setError('Unable to verify payment. Please contact support with transaction ID: ' + transactionId);
          setLoading(false);
        });
      
      return; // Exit early for parameter-based flow
    }
    
    if (storedPaymentData && storedToken) {
      // We have payment data from the payment flow - enhance with HighLevel data
      const enhancedPaymentData = {
        ...storedPaymentData,
        transactionId: transactionId || 'N/A',
        authorizationCode: authorizationCode || 'N/A',
        paymentStatus: paymentStatus || 'completed',
        orderId: orderId || 'N/A',
        // Override amount if provided by HighLevel
        amount: amount ? parseFloat(amount) : storedPaymentData.amount,
        // Preserve email from stored payment data
        email: storedPaymentData.email
      };
      
      setPaymentData(enhancedPaymentData);
      setLoading(false);
    } else {
      // No stored payment data found - this shouldn't happen in normal flow
      setError('Payment data not found. Please try the payment process again.');
      setLoading(false);
    }
  }, [searchParams]);

  const handleContinueToAccountSetup = () => {
    // Redirect to registration flow with payment verified flag
    navigate('/register?payment_verified=true&userType=Homeowner');
  };

  const handleContactSupport = () => {
    window.open('mailto:support@homedesignguild.com?subject=Payment%20Confirmation%20Issue', '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductDisplayName = (tier) => {
    const names = {
      guild: 'Guild Membership',
      guild_plus: 'Guild+ Membership',
      Guild: 'Guild Membership',
      GuildPlus: 'Guild+ Membership'
    };
    return names[tier] || tier || 'Premium Membership';
  };

  const getProductFeatures = (tier) => {
    const guildFeatures = [
      'Curated Project Gallery',
      'Full Access to Guild Guide Articles',
      'Powerful Filters',
      'Create & Save Idea Books'
    ];

    const guildPlusFeatures = [
      'Curated Project Gallery',
      'Full Access to Guild Guide Articles', 
      'Powerful Filters',
      'Create & Save Idea Books',
      'Smart Guild Matching',
      'Educational Content Access'
    ];

    if (tier === 'Guild' || tier === 'guild') {
      return guildFeatures;
    } else if (tier === 'GuildPlus' || tier === 'guild_plus') {
      return guildPlusFeatures;
    }
    
    return guildFeatures; // Default to Guild features
  };

  if (loading) {
    return (
      <Container maxWidth="md" className="payment-confirmation-page">
        <Box className="loading-container">
          <CircularProgress size={60} sx={{ mb: 2, color: '#00A06D' }} />
          <Typography variant="h6">Loading your order details...</Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we prepare your confirmation
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" className="payment-confirmation-page">
        <Fade in={true}>
          <Card className="confirmation-card error-card">
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Error sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Payment Information Not Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  If you just completed a payment, please try refreshing the page or 
                  contact our support team for assistance.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContactSupport}
                  sx={{ minWidth: 140 }}
                >
                  Contact Support
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/registration')}
                  sx={{ minWidth: 140 }}
                >
                  Start Registration
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className="payment-confirmation-page">
      
      <Fade in={true} timeout={600}>
        <Card className="confirmation-card success-card">
          <CardContent sx={{ p: 3 }}>
            
            {/* Development Banner */}
            {isMockData && (
              <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  <strong>Dev Mode:</strong> Mock payment data for testing
                </Typography>
              </Alert>
            )}

            {/* Success Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {/* Home Design Guild Logo */}
              <Box sx={{ mb: 2 }}>
                <img 
                  src="/Seal.svg" 
                  alt="Home Design Guild" 
                  style={{ 
                    width: '80px', 
                    height: '80px',
                    objectFit: 'contain'
                  }} 
                />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1c1c1c', mb: 2 }}>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Welcome to Home Design Guild
              </Typography>
              <Chip 
                label="Order Confirmed" 
                sx={{ backgroundColor: '#00A06D', color: 'white', fontWeight: 600 }}
                size="small"
              />
            </Box>

            {/* Compact Order Details */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {getProductDisplayName(paymentData?.planType)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#00A06D' }}>
                  ${paymentData?.amount || '4.99'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <Email sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  {paymentData?.email ? `Account: ${paymentData.email}` : 'Will be set during registration'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#00A06D', fontWeight: 600 }}>
                  <CheckCircle sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Confirmed
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                <CalendarToday sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                {formatDate(paymentData?.timestamp)}
              </Typography>
              
              {/* Transaction Details */}
              {paymentData?.transactionId && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    <strong>Transaction ID:</strong> {paymentData.transactionId}
                  </Typography>
                  {paymentData?.authorizationCode && paymentData.authorizationCode !== 'N/A' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <strong>Authorization:</strong> {paymentData.authorizationCode}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Compact Features List */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, color: '#1c1c1c', mb: 1, fontSize: '14px' }}>
                Your Membership Includes:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {getProductFeatures(paymentData?.planType).slice(0, 4).map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutline sx={{ color: '#00A06D', fontSize: 16, mr: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '14px !important', lineHeight: 1.2 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {getProductFeatures(paymentData?.planType).length > 4 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  + {getProductFeatures(paymentData?.planType).length - 4} more features
                </Typography>
              )}
            </Box>

            {/* Continue Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Ready to Complete Your Account?
              </Typography>
              {paymentData?.email && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We'll use <strong>{paymentData.email}</strong> for your account
                </Typography>
              )}
              
              <Button
                variant="contained"
                size="large"
                onClick={handleContinueToAccountSetup}
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  backgroundColor: '#1c1c1c',
                  color: '#ffffff',
                  boxShadow: '0 3px 15px rgba(28, 28, 28, 0.3)',
                  '&:hover': {
                    backgroundColor: '#333333',
                    boxShadow: '0 4px 20px rgba(28, 28, 28, 0.4)',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                Continue to Account Setup
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Email verification and profile setup
              </Typography>
            </Box>

          </CardContent>
        </Card>
      </Fade>
    </Container>
  );
};

export default PaymentConfirmation;
